import React, { useEffect, useMemo, useState } from 'react';
import { Heart, Trophy, ShieldCheck, Loader2, Send, Inbox, Sparkles, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import GiveVoteModal from '@/components/GiveVoteModal';

const initials = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((p) => p[0].toUpperCase())
  .join('');

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

const currentMonthKey = () => new Date().toISOString().slice(0, 7);

const prettyMonth = (mk) => {
  if (!mk) return '';
  try {
    return new Date(`${mk}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return mk;
  }
};

function VoteRow({ vote, side }) {
  // side = 'given' shows recipient; 'received' shows giver.
  const other = side === 'given'
    ? { id: vote.to_user_id, name: vote.to_user_name }
    : { id: vote.from_user_id, name: vote.from_user_name };
  const label = side === 'given' ? 'To' : 'From';
  return (
    <li className="py-3 flex items-start gap-3" data-testid={`vote-row-${vote.id}`}>
      <span className="inline-flex h-9 w-9 rounded-full bg-amber-500/15 border border-amber-500/30 items-center justify-center text-xs font-bold text-amber-100 flex-shrink-0">
        {initials(other.name)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
          <span className="font-semibold text-white truncate">{other.name}</span>
          <span className="text-[11px] text-slate-500">· {formatWhen(vote.created_at || vote.date)}</span>
        </div>
        {vote.note && (
          <div className="mt-1 text-sm text-slate-300 italic">“{vote.note}”</div>
        )}
      </div>
    </li>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="py-10 flex flex-col items-center text-center gap-2">
      <Icon className="h-8 w-8 text-amber-300/60" />
      <div className="text-white font-semibold">{title}</div>
      <div className="text-xs text-slate-400 max-w-sm">{subtitle}</div>
    </div>
  );
}

export default function PeerKudosPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Owner' || user?.role === 'Admin';

  const [tab, setTab] = useState('received');
  const [given, setGiven] = useState([]);
  const [received, setReceived] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [status, setStatus] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Admin state
  const [adminMonth, setAdminMonth] = useState(currentMonthKey());
  const [adminVotes, setAdminVotes] = useState([]);
  const [adminBoard, setAdminBoard] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSearch, setAdminSearch] = useState('');
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealTopN, setRevealTopN] = useState(3);
  const [revealing, setRevealing] = useState(false);

  const loadMine = async () => {
    setLoadingMine(true);
    try {
      const [g, r, s, rv] = await Promise.all([
        api.get('/votes/mine?direction=given'),
        api.get('/votes/mine?direction=received'),
        api.get('/votes/my-status'),
        api.get('/votes/reveal/latest'),
      ]);
      setGiven(g.data?.votes || []);
      setReceived(r.data?.votes || []);
      setStatus(s.data);
      setReveal(rv.data?.reveal || null);
    } catch (_e) {
      // silent
    } finally {
      setLoadingMine(false);
    }
  };

  const loadAdmin = async (m) => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const [all, lb] = await Promise.all([
        api.get(`/votes/admin/all?month=${m}`),
        api.get(`/votes/admin/leaderboard?month=${m}`),
      ]);
      setAdminVotes(all.data?.votes || []);
      setAdminBoard(lb.data?.leaderboard || []);
    } catch (_e) {
      setAdminVotes([]);
      setAdminBoard([]);
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => { loadMine(); }, []);
  useEffect(() => { if (isAdmin) loadAdmin(adminMonth); }, [isAdmin, adminMonth]);

  const filteredAdminVotes = useMemo(() => {
    if (!adminSearch.trim()) return adminVotes;
    const q = adminSearch.trim().toLowerCase();
    return adminVotes.filter((v) =>
      (v.from_user_name || '').toLowerCase().includes(q)
      || (v.to_user_name || '').toLowerCase().includes(q)
      || (v.note || '').toLowerCase().includes(q)
    );
  }, [adminVotes, adminSearch]);

  const totalGivenMonth = status?.votes_given_this_month ?? 0;
  const totalReceivedMonth = status?.votes_received_this_month ?? 0;
  const votesLeftToday = status?.votes_remaining_today ?? 3;

  const doReveal = async () => {
    setRevealing(true);
    try {
      const r = await api.post(`/votes/admin/reveal?month=${adminMonth}&top_n=${revealTopN}`);
      toast.success('Top recipients revealed to the whole team.');
      setReveal(r.data?.reveal);
      setRevealOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reveal failed');
    } finally {
      setRevealing(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={Heart}
        title="Peer Kudos"
        subtitle="Give a little recognition where it's due. 3 votes a day, resets at midnight IST."
        actions={
          <Button
            onClick={() => setModalOpen(true)}
            disabled={votesLeftToday <= 0}
            data-testid="kudos-give-btn-page"
            className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            {votesLeftToday <= 0 ? 'All 3 used today' : `Give a vote (${votesLeftToday} left)`}
          </Button>
        }
      />

      {reveal && (
        <Card className="shine-card p-4 rounded-xl mb-4 border-amber-500/40" data-testid="kudos-page-reveal">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-xl flex-shrink-0">🏆</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1">
                <Trophy className="h-3 w-3" /> {prettyMonth(reveal.month_key)} — Peer recognition
              </div>
              <div className="text-sm text-white font-semibold mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                {(reveal.top_recipients || []).map((r, i) => (
                  <span key={r.user_id}>
                    <span className="text-amber-200">{['🥇', '🥈', '🥉'][i] || '⭐'} {r.name}</span>
                    <span className="text-slate-400 font-normal text-xs"> ({r.vote_count})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Votes left today</div>
          <div className="mt-1 text-2xl font-extrabold text-white" data-testid="stat-left-today">{votesLeftToday}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Given · this month</div>
          <div className="mt-1 text-2xl font-extrabold text-white" data-testid="stat-given-month">{totalGivenMonth}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Received · this month</div>
          <div className="mt-1 text-2xl font-extrabold text-white" data-testid="stat-received-month">{totalReceivedMonth}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Daily limit</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{status?.daily_limit ?? 3}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="kudos-tabs">
        <TabsList className="bg-[#0a1628] border border-amber-500/20">
          <TabsTrigger value="received" data-testid="tab-received" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <Inbox className="h-4 w-4 mr-1.5" /> Received ({received.length})
          </TabsTrigger>
          <TabsTrigger value="given" data-testid="tab-given" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <Send className="h-4 w-4 mr-1.5" /> Given ({given.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" data-testid="tab-admin" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Admin Overview
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="received" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            {loadingMine ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
            ) : received.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="No kudos yet"
                subtitle="When teammates recognise your work, their votes will show up here — with any note they left for you."
              />
            ) : (
              <ul className="divide-y divide-amber-500/5" data-testid="received-list">
                {received.map((v) => <VoteRow key={v.id} vote={v} side="received" />)}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="given" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            {loadingMine ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
            ) : given.length === 0 ? (
              <EmptyState
                icon={Send}
                title="You haven't given a vote yet"
                subtitle="You have 3 votes a day — send one to a teammate who did great work."
              />
            ) : (
              <ul className="divide-y divide-amber-500/5" data-testid="given-list">
                {given.map((v) => <VoteRow key={v.id} vote={v} side="given" />)}
              </ul>
            )}
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="admin" className="mt-4 space-y-4">
            <Card className="shine-card p-4 rounded-xl">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-amber-300" />
                  <span className="text-sm text-slate-300">Full audit — private votes visible only here.</span>
                </div>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                  <Input
                    type="month"
                    value={adminMonth}
                    onChange={(e) => setAdminMonth(e.target.value)}
                    className="w-40 bg-[#0f2240] border-amber-500/20 text-white h-9"
                    data-testid="admin-month-input"
                  />
                  <Button
                    onClick={() => setRevealOpen(true)}
                    disabled={adminBoard.length === 0}
                    data-testid="admin-reveal-btn"
                    className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold disabled:opacity-50 h-9"
                  >
                    <Trophy className="h-4 w-4 mr-1.5" /> Reveal top for {prettyMonth(adminMonth)}
                  </Button>
                </div>
              </div>
            </Card>

            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="shine-card p-5 rounded-xl lg:col-span-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-white flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-300" /> Leaderboard</h3>
                  <span className="text-xs text-slate-500">{prettyMonth(adminMonth)}</span>
                </div>
                {adminLoading ? (
                  <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-amber-300" /></div>
                ) : adminBoard.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4">No votes yet in {prettyMonth(adminMonth)}.</div>
                ) : (
                  <ol className="space-y-2" data-testid="admin-leaderboard">
                    {adminBoard.map((r, i) => (
                      <li key={r.user_id} className="flex items-center gap-2 rounded-lg bg-[#0f2240] px-3 py-2 border border-amber-500/10">
                        <span className="w-6 text-lg leading-none">{['🥇', '🥈', '🥉'][i] || `${i + 1}.`}</span>
                        <span className="inline-flex h-7 w-7 rounded-full bg-amber-500/15 items-center justify-center text-[10px] font-bold text-amber-100">
                          {initials(r.name)}
                        </span>
                        <span className="flex-1 min-w-0 truncate text-sm text-white font-semibold">{r.name}</span>
                        <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[11px]">{r.vote_count}</Badge>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>

              <Card className="shine-card p-5 rounded-xl lg:col-span-2">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <h3 className="font-bold text-white">All votes · {prettyMonth(adminMonth)}</h3>
                  <div className="relative w-full max-w-xs">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      value={adminSearch}
                      onChange={(e) => setAdminSearch(e.target.value)}
                      placeholder="Filter by giver, receiver or note…"
                      className="pl-7 bg-[#0f2240] border-amber-500/20 text-white h-8 text-sm"
                      data-testid="admin-search-input"
                    />
                  </div>
                </div>
                {adminLoading ? (
                  <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-amber-300" /></div>
                ) : filteredAdminVotes.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4">No votes match.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-votes-table">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
                          <th className="py-2 pr-3">When</th>
                          <th className="py-2 pr-3">Giver</th>
                          <th className="py-2 pr-3">Recipient</th>
                          <th className="py-2 pr-3">Note</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-500/5">
                        {filteredAdminVotes.map((v) => (
                          <tr key={v.id} data-testid={`admin-vote-row-${v.id}`}>
                            <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{formatWhen(v.created_at || v.date)}</td>
                            <td className="py-2 pr-3 text-white font-medium truncate">{v.from_user_name}</td>
                            <td className="py-2 pr-3 text-amber-200 font-medium truncate">{v.to_user_name}</td>
                            <td className="py-2 pr-3 text-slate-300 italic truncate max-w-[220px]">{v.note || <span className="text-slate-600 not-italic">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <GiveVoteModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        currentUserId={user?.id}
        onGiven={() => { loadMine(); if (isAdmin) loadAdmin(adminMonth); }}
      />

      <AlertDialog open={revealOpen} onOpenChange={setRevealOpen}>
        <AlertDialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-300" /> Reveal top voter(s) for {prettyMonth(adminMonth)}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              This publishes the top recipients to the <span className="text-white font-semibold">whole team</span> and sends everyone a notification.
              It cannot be quietly undone — you'd have to re-reveal with different numbers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-3">
            <label className="text-xs text-slate-400 flex items-center gap-2">
              How many top recipients to reveal?
              <select
                value={revealTopN}
                onChange={(e) => setRevealTopN(Number(e.target.value))}
                className="bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-1 text-white text-sm"
                data-testid="reveal-top-n"
              >
                {[1, 2, 3, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-amber-500/20 text-slate-200 hover:bg-amber-500/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReveal}
              disabled={revealing}
              data-testid="reveal-confirm-btn"
              className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"
            >
              {revealing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reveal to team'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
